import React, { useMemo } from 'react';
import { Row, Col, Statistic, Card, Empty, DatePicker, Space, Typography, Alert } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import dayjs from 'dayjs';
import { parseApiDate } from '../../utils/parseApiDate';
import './AssistantStatisticsContent.css';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#64748b'];

const CHART_GRID = '#e2e8f0';
const CHART_TICK = '#64748b';
const CHART_TOOLTIP = {
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
};

function mergeActivitySeries(statsData) {
  const api = statsData?.requests_over_time || [];
  const chat = statsData?.platform_chat_over_time || [];
  const bot = statsData?.bot_over_time || [];
  const map = new Map();
  const fmt = (ts) => {
    const d = parseApiDate(ts);
    return d
      ? d.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '';
  };
  for (const p of api) {
    const t = p.timestamp;
    if (!map.has(t)) {
      map.set(t, { timestamp: t, time: fmt(t), apiRequests: 0, chatMessages: 0, botRequests: 0 });
    }
    map.get(t).apiRequests = p.count ?? 0;
  }
  for (const p of chat) {
    const t = p.timestamp;
    if (!map.has(t)) {
      map.set(t, { timestamp: t, time: fmt(t), apiRequests: 0, chatMessages: 0, botRequests: 0 });
    }
    map.get(t).chatMessages = p.count ?? 0;
  }
  for (const p of bot) {
    const t = p.timestamp;
    if (!map.has(t)) {
      map.set(t, { timestamp: t, time: fmt(t), apiRequests: 0, chatMessages: 0, botRequests: 0 });
    }
    map.get(t).botRequests = p.count ?? 0;
  }
  return [...map.values()].sort((a, b) => (parseApiDate(a.timestamp)?.getTime() || 0) - (parseApiDate(b.timestamp)?.getTime() || 0));
}

function AssistantStatisticsContent({
  statsData,
  loading,
  dateRange,
  onDateRangeChange,
}) {
  const activitySeries = useMemo(() => mergeActivitySeries(statsData), [statsData]);

  const endpointBars = useMemo(() => {
    const m = statsData?.requests_by_endpoint || {};
    return Object.entries(m)
      .map(([name, value]) => ({ name: name.length > 36 ? `${name.slice(0, 34)}…` : name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 12);
  }, [statsData]);

  const statusPie = useMemo(() => {
    const m = statsData?.requests_by_status || {};
    return Object.entries(m).map(([name, value]) => ({ name: String(name), value }));
  }, [statsData]);

  const errorRows = useMemo(() => {
    const m = statsData?.error_breakdown || {};
    return Object.entries(m)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [statsData]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Text type="secondary">Loading statistics…</Text>
      </div>
    );
  }

  if (!statsData) {
    return <Empty description="No data" />;
  }

  const total = statsData.total_requests ?? 0;
  const success = statsData.successful_requests ?? 0;
  const failed = statsData.failed_requests ?? 0;
  const chatTotal = statsData.platform_chat_messages ?? 0;
  const chatUser = statsData.platform_chat_user_messages ?? 0;
  const chatAsst = statsData.platform_chat_assistant_messages ?? 0;
  const botAnswers = statsData.bot_answer_requests ?? 0;
  const botRefused = statsData.bot_refused_requests ?? 0;
  const botFailed = statsData.bot_failed_requests ?? 0;
  const botMsgTotal = statsData.bot_messages_total ?? 0;
  const botUser = statsData.bot_user_messages ?? 0;
  const botAsst = statsData.bot_assistant_messages ?? 0;
  const hasActivityChart =
    activitySeries.length > 0 &&
    activitySeries.some((r) => r.apiRequests > 0 || r.chatMessages > 0 || r.botRequests > 0);

  return (
    <div className="assistant-statistics-root">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message="What these numbers mean"
          description={
            <>
              <strong>API</strong> counts are HTTP requests logged for <strong>API keys</strong> linked to this assistant
              (including widget traffic that uses those keys). <strong>Platform chat</strong> counts messages stored from
              the in-app <strong>/chat</strong> UI for this assistant (user + assistant turns).{' '}
              <strong>Bots</strong> counts Telegram (and other channel) answer attempts logged for bots bound to this assistant.
              {total === 0 && chatTotal === 0 && botAnswers === 0 && botRefused === 0 && (
                <span>
                  {' '}
                  Use the chat UI or create an API key under <strong>Integrations</strong> to generate activity.
                </span>
              )}
            </>
          }
        />

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <Text type="secondary">Date range</Text>
          <RangePicker
            value={dateRange}
            onChange={(dates) => onDateRangeChange(dates)}
            allowClear={false}
            presets={[
              { label: '7d', value: [dayjs().subtract(7, 'day'), dayjs()] },
              { label: '30d', value: [dayjs().subtract(30, 'day'), dayjs()] },
              { label: '90d', value: [dayjs().subtract(90, 'day'), dayjs()] },
            ]}
          />
        </div>

        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            API (keys &amp; channels)
          </Text>
          <Row gutter={[12, 12]}>
            <Col xs={12} sm={8} lg={4}>
              <Card size="small" bordered={false} className="assistant-stat-kpi-card">
                <Statistic title="Total requests" value={total} />
              </Card>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Card size="small" bordered={false} className="assistant-stat-kpi-card">
                <Statistic title="Successful" value={success} valueStyle={{ color: '#059669' }} />
              </Card>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Card size="small" bordered={false} className="assistant-stat-kpi-card">
                <Statistic title="Failed" value={failed} valueStyle={{ color: failed ? '#dc2626' : undefined }} />
              </Card>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Card size="small" bordered={false} className="assistant-stat-kpi-card">
                <Statistic
                  title="Success rate"
                  suffix="%"
                  value={total ? (100 - (statsData.error_rate || 0)).toFixed(1) : '—'}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Card size="small" bordered={false} className="assistant-stat-kpi-card">
                <Statistic title="Avg latency" suffix="ms" value={statsData.avg_latency_ms ?? 0} />
              </Card>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Card size="small" bordered={false} className="assistant-stat-kpi-card">
                <Statistic
                  title="Avg TTFT"
                  suffix="ms"
                  value={statsData.avg_ttft_ms != null ? statsData.avg_ttft_ms : '—'}
                />
              </Card>
            </Col>
          </Row>
        </div>

        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Platform chat (in-app /chat)
          </Text>
          <Row gutter={[12, 12]}>
            <Col xs={12} sm={8}>
              <Card size="small" bordered={false} className="assistant-stat-kpi-card">
                <Statistic title="Messages (all roles)" value={chatTotal} />
              </Card>
            </Col>
            <Col xs={12} sm={8}>
              <Card size="small" bordered={false} className="assistant-stat-kpi-card">
                <Statistic title="User messages" value={chatUser} valueStyle={{ color: '#2563eb' }} />
              </Card>
            </Col>
            <Col xs={12} sm={8}>
              <Card size="small" bordered={false} className="assistant-stat-kpi-card">
                <Statistic title="Assistant messages" value={chatAsst} valueStyle={{ color: '#059669' }} />
              </Card>
            </Col>
          </Row>
        </div>

        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Bots (Telegram &amp; channels)
          </Text>
          <Row gutter={[12, 12]}>
            <Col xs={12} sm={8} lg={4}>
              <Card size="small" bordered={false} className="assistant-stat-kpi-card">
                <Statistic title="Answers sent" value={botAnswers} valueStyle={{ color: '#059669' }} />
              </Card>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Card size="small" bordered={false} className="assistant-stat-kpi-card">
                <Statistic title="Declined" value={botRefused} />
              </Card>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Card size="small" bordered={false} className="assistant-stat-kpi-card">
                <Statistic
                  title="Errors"
                  value={botFailed}
                  valueStyle={{ color: botFailed ? '#dc2626' : undefined }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Card size="small" bordered={false} className="assistant-stat-kpi-card">
                <Statistic title="Messages (all roles)" value={botMsgTotal} />
              </Card>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Card size="small" bordered={false} className="assistant-stat-kpi-card">
                <Statistic title="User messages" value={botUser} valueStyle={{ color: '#2563eb' }} />
              </Card>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Card size="small" bordered={false} className="assistant-stat-kpi-card">
                <Statistic title="Assistant messages" value={botAsst} valueStyle={{ color: '#059669' }} />
              </Card>
            </Col>
          </Row>
        </div>

        <Card
          title="Activity over time"
          size="small"
          className="assistant-stat-chart-card"
          styles={{ body: { paddingTop: 8 } }}
        >
          <div className="assistant-stat-chart-body" style={{ height: 280 }}>
            {hasActivityChart ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={activitySeries} margin={{ top: 8, right: 12, left: 4, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} />
                  <XAxis dataKey="time" tick={{ fontSize: 11, fill: CHART_TICK }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11, fill: CHART_TICK }} allowDecimals={false} />
                  <Tooltip contentStyle={CHART_TOOLTIP} />
                  <Legend
                    wrapperStyle={{ fontSize: 12 }}
                    formatter={(value) => <span style={{ color: '#334155' }}>{value}</span>}
                  />
                  <Line
                    type="monotone"
                    dataKey="apiRequests"
                    name="API requests"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="chatMessages"
                    name="Platform chat messages (hourly)"
                    stroke="#059669"
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="botRequests"
                    name="Bot answer attempts (hourly)"
                    stroke="#8b5cf6"
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No API, platform chat, or bot activity in this period for the selected range."
              />
            )}
          </div>
        </Card>

        <Row gutter={[12, 12]}>
          <Col xs={24} xl={14}>
            <Card title="By endpoint" size="small" className="assistant-stat-chart-card" styles={{ body: { paddingTop: 8 } }}>
              {endpointBars.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No endpoint breakdown" />
              ) : (
                <div className="assistant-stat-bar-wrap">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={endpointBars}
                      layout="vertical"
                      margin={{ left: 4, right: 12, top: 8, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11, fill: CHART_TICK }} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={118}
                        tick={{ fontSize: 10, fill: CHART_TICK }}
                        interval={0}
                      />
                      <Tooltip contentStyle={CHART_TOOLTIP} />
                      <Bar dataKey="value" fill="#2563eb" radius={[0, 4, 4, 0]} name="Requests" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </Col>
          <Col xs={24} xl={10}>
            <Card title="By HTTP status" size="small" className="assistant-stat-chart-card" styles={{ body: { paddingTop: 8 } }}>
              {statusPie.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No status data" />
              ) : (
                <div className="assistant-stat-pie-wrap">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusPie}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={72}
                        paddingAngle={0.5}
                        label={false}
                      >
                        {statusPie.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={CHART_TOOLTIP} />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        formatter={(value) => <span style={{ color: '#334155', fontSize: 12 }}>{value}</span>}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </Col>
        </Row>

        {errorRows.length > 0 && (
          <Card title="Error types" size="small" className="assistant-stat-chart-card">
            <Row gutter={[8, 8]}>
              {errorRows.map((row) => (
                <Col xs={24} sm={12} key={row.name}>
                  <Text>
                    <strong>{row.name}</strong>: {row.value}
                  </Text>
                </Col>
              ))}
            </Row>
          </Card>
        )}
      </Space>
    </div>
  );
}

export default AssistantStatisticsContent;
